import React, { useCallback, useEffect, useState } from 'react';

import AppBar from 'components/AppBar';
import GroupBadges from 'components/Badges/GroupBadges';
import Button from 'components/Buttons/Button';
import ProgressSteps from 'components/ProgressSteps';
import { Desktop, Mobile } from 'components/Responsive';
import { WizardProps } from 'components/Wizard';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { Badges } from 'models/groupedList';
import { createOnboarding, CreateOnboarding, OnboardingUser } from 'services/onboardingService';
import { getFormattedPrice } from 'utils/currency';
import { extractErrorMessage, formatTrim } from 'utils/generic';

import {
  Container,
  PaddingContainer,
  Header,
  Subtitle,
  GroupButton,
  ReviewContainer,
  IconReview,
  InfoRulesReview,
  BottomReviewBackground,
  BottomReviewImage,
} from '../../styles';

interface Props {
  wizard: WizardProps;
}

const MAX_PRICE_BY_CURRENCY = {
  BRL: {
    flight: {
      national: 540,
      international: 6_500,
    },
    hotel: {
      national: 540,
      international: 1_100,
    },
    vehicle: {
      national: 250,
      international: 1_100,
    },
  },
  ARS: {
    flight: {
      national: 8_000,
      international: 70_000,
    },
    hotel: {
      national: 7_000,
      international: 10_000,
    },
    vehicle: {
      national: 7_000,
      international: 10_000,
    },
  },
  COP: {
    flight: {
      national: 400_000,
      international: 3_500_000,
    },
    hotel: {
      national: 370_000,
      international: 560_000,
    },
    vehicle: {
      national: 150_000,
      international: 450_000,
    },
  },
  MXN: {
    flight: {
      national: 2_250,
      international: 22_500,
    },
    hotel: {
      national: 2_250,
      international: 4_500,
    },
    vehicle: {
      national: 1_450,
      international: 4_500,
    },
  },
  USD: {
    flight: {
      national: 100,
      international: 1_000,
    },
    hotel: {
      national: 100,
      international: 150,
    },
    vehicle: {
      national: 120,
      international: 150,
    },
  },
};

const ReviewOnboarding = ({ wizard }: Props) => {
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const toast = useToast();
  const [joinUsers, setJoinUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const currency = user?.countryCurrency || 'COP';

  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency, t });
    return formatted;
  };

  useEffect(() => {
    const listUsers = wizard.data.users
      .filter(
        (user: any) =>
          !wizard.data.approvers.some((approver: any) => formatTrim(approver.email) === formatTrim(user.email)),
      )
      .map((user: any) => ({
        ...user,
        sequence: 0,
        approver: false,
        groupUser: true,
      }));

    const listApprovers = wizard.data.approvers.map((approver: any) => {
      if (wizard.data.approvalType === 'SIMPLE') {
        return {
          ...approver,
          sequence: 0,
          approver: true,
          groupUser: wizard.data.users.some((user: any) => formatTrim(user.email) === formatTrim(approver.email)),
        };
      }
      return {
        ...approver,
        approver: true,
        groupUser: wizard.data.users.some((user: any) => formatTrim(user.email) === formatTrim(approver.email)),
      };
    });

    setJoinUsers(
      listUsers.concat(listApprovers).map((approver: any) => ({
        ...approver,
        groupName: wizard.data.group.name,
      })),
    );
  }, [wizard]);

  const goBack = useCallback(() => {
    if (wizard.data.approvalType === 'PARALLEL' || wizard.data.approvalType === 'SEQUENTIAL') {
      wizard.previousStep();
    } else {
      wizard.goToStep(3);
    }
  }, [wizard]);

  const handleSave = useCallback(() => {
    setIsLoading(true);
    createOnboarding({
      groupName: wizard.data.group.name,
      ruleName: wizard.data.ruleName,
      groupType: wizard.data.approvalType,
      onboardingUsers: joinUsers as OnboardingUser[],
    } as CreateOnboarding)
      .then((response) => {
        if (response) {
          wizard.nextStep();
        } else {
          toast.add({ type: 'danger', message: t('defaultError.default') });
        }
        setIsLoading(false);
      })
      .catch((error: Error) => {
        toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
        setIsLoading(false);
      });
  }, [joinUsers, wizard, t, toast]);

  return (
    <Container>
      <nav>
        <AppBar goBack={goBack}>{t('reviewOnboarding.title')}</AppBar>
        <ProgressSteps size={wizard.size} currentStep={wizard.currentStep} />
      </nav>
      <PaddingContainer>
        <Header>
          <h3>{t('reviewOnboarding.header')}</h3>
          <Subtitle>
            {t('reviewOnboarding.subtitle')} <strong>{t('reviewOnboarding.subtitleStrong')}</strong>
          </Subtitle>
        </Header>
        <ReviewContainer>
          <IconReview>
            <img alt="group-info-review" src="/images/profile-icons/circle.svg" />
          </IconReview>
          <h3>{t('reviewOnboarding.group')}</h3>
          <h2>{wizard.data.group.name}</h2>
          <GroupBadges
            lineGroup={false}
            itemsBadges={joinUsers.map(
              (badge: any) =>
                ({
                  id: formatTrim(badge.email),
                  name: badge.name,
                  lastName: badge.lastName,
                } as Badges),
            )}
          />
        </ReviewContainer>
        <ReviewContainer>
          <IconReview>
            <img alt="group-info-review" src="/images/profile-icons/circle.svg" />
          </IconReview>
          <h3>{t('reviewOnboarding.policy')}</h3>
          <h2>{wizard.data.ruleName}</h2>
          <InfoRulesReview>
            <h4>{t('reviewOnboarding.nationalFlights')}</h4>
            <p>
              {t('reviewOnboarding.minimumAnticipation')}{' '}
              <strong>{t('reviewOnboarding.minimumAnticipationDays', { days: 7 })}</strong>
            </p>
            <p>
              {t('reviewOnboarding.airMaxPrice')}{' '}
              <strong>{formattedPrice(MAX_PRICE_BY_CURRENCY[currency].flight.national)}</strong>
            </p>
            <p>
              {t('reviewOnboarding.percentagePrice')} <strong>35%</strong>
            </p>
          </InfoRulesReview>
          <InfoRulesReview>
            <h4>{t('reviewOnboarding.internationalFlights')}</h4>
            <p>
              {t('reviewOnboarding.minimumAnticipation')}{' '}
              <strong>{t('reviewOnboarding.minimumAnticipationDays', { days: 14 })}</strong>
            </p>
            <p>
              {t('reviewOnboarding.airMaxPrice')}{' '}
              <strong>{formattedPrice(MAX_PRICE_BY_CURRENCY[currency].flight.international)}</strong>
            </p>
            <p>
              {t('reviewOnboarding.percentagePrice')} <strong>25%</strong>
            </p>
          </InfoRulesReview>
          <InfoRulesReview>
            <h4>{t('reviewOnboarding.nationalHotel')}</h4>
            <p>
              {t('reviewOnboarding.minimumAnticipation')}{' '}
              <strong>{t('reviewOnboarding.minimumAnticipationDays', { days: 7 })}</strong>
            </p>
            <p>
              {t('reviewOnboarding.hotelMaxPrice')}{' '}
              <strong>{formattedPrice(MAX_PRICE_BY_CURRENCY[currency].hotel.national)}</strong>
            </p>
          </InfoRulesReview>
          <InfoRulesReview>
            <h4>{t('reviewOnboarding.internationalHotel')}</h4>
            <p>
              {t('reviewOnboarding.minimumAnticipation')}{' '}
              <strong>{t('reviewOnboarding.minimumAnticipationDays', { days: 14 })}</strong>
            </p>
            <p>
              {t('reviewOnboarding.hotelMaxPrice')}{' '}
              <strong>{formattedPrice(MAX_PRICE_BY_CURRENCY[currency].hotel.international)}</strong>
            </p>
          </InfoRulesReview>
          <InfoRulesReview>
            <h4>{t('reviewOnboarding.nationalVehicle')}</h4>
            <p>
              {t('reviewOnboarding.minimumAnticipation')}{' '}
              <strong>{t('reviewOnboarding.minimumAnticipationDays', { days: 7 })}</strong>
            </p>
            <p>
              {t('reviewOnboarding.vehicleMaxPrice')}{' '}
              <strong>{formattedPrice(MAX_PRICE_BY_CURRENCY[currency].vehicle.national)}</strong>
            </p>
          </InfoRulesReview>
          <InfoRulesReview>
            <h4>{t('reviewOnboarding.internationalVehicle')}</h4>
            <p>
              {t('reviewOnboarding.minimumAnticipation')}{' '}
              <strong>{t('reviewOnboarding.minimumAnticipationDays', { days: 14 })}</strong>
            </p>
            <p>
              {t('reviewOnboarding.vehicleMaxPrice')}{' '}
              <strong>{formattedPrice(MAX_PRICE_BY_CURRENCY[currency].vehicle.international)}</strong>
            </p>
          </InfoRulesReview>
        </ReviewContainer>
      </PaddingContainer>

      <Mobile>
        <BottomReviewBackground />
      </Mobile>
      <Desktop>
        <BottomReviewImage src="/images/onboarding/group-8-desktop.png" />
      </Desktop>
      <GroupButton marginBottom>
        <Button loading={isLoading} onClick={handleSave} disabled={false} value={t('reviewOnboarding.nextButton')} />
      </GroupButton>
    </Container>
  );
};

ReviewOnboarding.defaultProps = {
  wizard: {},
};

export default ReviewOnboarding;
